// @flow
export const MarkerPopupPosition = Object.freeze({
    TOP: 'Top',
    BOTTOM: 'Bottom',
    RIGHT: 'Right',
    LEFT: 'Left',
    CENTER: 'Center'
});

export type MarkerPositionType = $Values<typeof MarkerPopupPosition>;

export const getPopupPosition = (
    marker: HTMLDivElement,
    parentContainer: HTMLDivElement,
    popup: HTMLDivElement
): {
    position: MarkerPositionType,
    offset?: { top?: number, bottom?: number },
    style: Object
} => {
    const markerBounds = marker.getBoundingClientRect();
    const parentBounds = parentContainer.getBoundingClientRect();
    const popupBounds = popup.getBoundingClientRect();
    const markerRelativePosition = {
        left: markerBounds.left - parentBounds.left,
        right: markerBounds.right - parentBounds.left,
        top: markerBounds.top - parentBounds.top,
        bottom: markerBounds.bottom - parentBounds.top
    };
    const additionalSpace = 20;
    const topSpace = markerRelativePosition.top - additionalSpace;
    const bottomSpace =
        parentBounds.height - markerRelativePosition.bottom - additionalSpace;
    const rightSpace =
        parentBounds.width - markerRelativePosition.right - additionalSpace;
    const leftSpace = markerRelativePosition.left - additionalSpace;

    const canPlaceOnTop = () => {
        return (
            topSpace > popupBounds.height &&
            popupBounds.width < parentBounds.width
        );
    };

    const canPlaceOnBottom = () => {
        return (
            bottomSpace > popupBounds.height &&
            popupBounds.width < parentBounds.width
        );
    };

    const canPlaceOnRight = () => {
        return (
            rightSpace > popupBounds.width &&
            popupBounds.height < parentBounds.height
        );
    };

    const canPlaceOnLeft = () => {
        return (
            markerRelativePosition.top + popupBounds.height / 2 <
                parentBounds.height &&
            leftSpace > popupBounds.width &&
            popupBounds.height < parentBounds.height
        );
    };

    if (canPlaceOnTop()) {
        const leftOffset = popupBounds.left - parentBounds.left;
        const rightOffset = parentBounds.right - popupBounds.right;

        const leftOffsetToAdjust = leftOffset < 0 ? -leftOffset : 0;
        const rightOffsetToAdjust = rightOffset < 0 ? -rightOffset : 0;

        const offsetString =
            leftSpace < rightSpace
                ? `+ ${leftOffsetToAdjust + 10}px`
                : `- ${rightOffsetToAdjust + 10}px`;

        const transform = `translate(calc(-50% ${offsetString}), calc(-100% - 30px))`;

        return {
            position: MarkerPopupPosition.TOP,
            style: {
                position: 'fixed',
                transform
            }
        };
    }
    if (canPlaceOnBottom()) {
        const leftOffset = popupBounds.left - parentBounds.left;
        const rightOffset = parentBounds.right - popupBounds.right;

        const leftOffsetToAdjust = leftOffset < 0 ? -leftOffset : 0;
        const rightOffsetToAdjust = rightOffset < 0 ? -rightOffset : 0;

        const offsetString =
            leftSpace < rightSpace
                ? `+ ${leftOffsetToAdjust + 10}px`
                : `- ${rightOffsetToAdjust + 10}px`;

        const transform = `translate(calc(-50% ${offsetString}), 10px)`;

        return {
            position: MarkerPopupPosition.BOTTOM,
            style: {
                position: 'fixed',
                transform
            }
        };
    }
    if (canPlaceOnRight()) {
        const popupTop = markerBounds.top - popupBounds.height / 2;
        const popupBottom = popupTop + popupBounds.height;

        const topOffset = popupTop - parentBounds.top;
        const bottomOffset = parentBounds.bottom - popupBottom;

        const topOffsetToAdjust = topOffset < 0 ? -topOffset : 0;
        const bottomOffsetToAdjust = bottomOffset < 0 ? -bottomOffset : 0;

        const offsetString =
            topSpace < bottomSpace
                ? `+ ${topOffsetToAdjust + 10}px`
                : `- ${bottomOffsetToAdjust + 10}px`;

        const transform = `translate(30px, calc(-50% ${offsetString}))`;

        return {
            position: MarkerPopupPosition.RIGHT,
            style: {
                position: 'fixed',
                transform
            }
        };
    }
    if (canPlaceOnLeft()) {
        const popupTop = markerBounds.top - popupBounds.height / 2;
        const popupBottom = popupTop + popupBounds.height;

        const topOffset = popupTop - parentBounds.top;
        const bottomOffset = parentBounds.bottom - popupBottom;

        const topOffsetToAdjust = topOffset < 0 ? -topOffset : 0;
        const bottomOffsetToAdjust = bottomOffset < 0 ? -bottomOffset : 0;

        const offsetString =
            topSpace < bottomSpace
                ? `+ ${topOffsetToAdjust + 10}px`
                : `- ${bottomOffsetToAdjust + 10}px`;

        const transform = `translate(calc(-100% - 10px), calc(-50% ${offsetString})`;

        return {
            position: MarkerPopupPosition.LEFT,
            style: {
                position: 'fixed',
                transform
            }
        };
    }

    return {
        position: MarkerPopupPosition.BOTTOM,
        style: {
            position: 'fixed',
            transform: 'translate(-50%, 10px)'
        }
    };
};

export default getPopupPosition;
